import { AxiosResponse } from "axios";
import { ApiResponse } from "middleware/api";
import { actions } from "reducers/regions";
import { actions as notificationActions } from "reducers/notification";
import { actions as sessionActions } from "reducers/session";
import * as regionsMiddleware from "middleware/regions";
import {
  call,
  CallEffect,
  ForkEffect,
  put,
  PutEffect,
  takeLatest,
} from "redux-saga/effects";
import {
  RegionDetailResponseType,
  RegionsResponseType,
  UpdateRegionDetailResponseType,
  WorkzonesResponseType,
  PricingResponseType,
  UpdateWorkzoneCapacityPayloadType,
  UpdatePricingResponseType,
  OriginsResponseType,
} from "./types/regions";
import { PayloadAction } from "@reduxjs/toolkit";
import { PolygonGeneralForm } from "pages/polygons/general/types";
import { NotificationStateType } from "reducers/types/notification";
import i18next from "config/i18next";
import { connectionErrorAlert } from "utils/alerts";
import { RainsResponseType } from "./types/rainModal";
import { PricingMiddlewareType, PricingType } from "reducers/types/regions";
import { TierType } from "pages/polygons/pricing/tiersForm/tiersTable/types";
import { serverErrorAlert } from "utils/alerts";
import { FilterTransactionsType } from "./types/transactions";

const sagas: ForkEffect<never>[] = [
  takeLatest(actions.getRegionsRequest.type, getRegions),
  takeLatest(actions.getOriginsFetch.type, getOrigins),
  takeLatest(actions.getWorkzonesFetch.type, getWorkzones),
  takeLatest(actions.getPricingDetailRequest.type, getPricingDetail),
  takeLatest(actions.getRegionDetailRequest.type, getRegionDetail),
  takeLatest(actions.updateRegionDetailRequest.type, updateRegionDetail),
  takeLatest(actions.updatePricingDetailRequest.type, updatePricingDetail),
  takeLatest(
    actions.updateWorkzoneCapacityRequest.type,
    updateWorkzoneCapacity
  ),
  takeLatest(actions.getNextRainRequest.type, getNextRain),
];

export default sagas;

function* getRegions(): Generator<
  | PutEffect<{ type: string }>
  | CallEffect<AxiosResponse<ApiResponse<RegionsResponseType>>>,
  void,
  ApiResponse<RegionsResponseType>
> {
  const response = yield call(regionsMiddleware.getRegions);
  if (response.status !== 200) {
    yield put(actions.getRegionsError());
  } else {
    const { result } = response.data;
    yield put(actions.getRegionsSuccess(result.items));
  }
}

function* getOrigins({
  payload,
}: PayloadAction<FilterTransactionsType>): Generator<
  | PutEffect<{ type: string }>
  | CallEffect<AxiosResponse<ApiResponse<OriginsResponseType>>>,
  void,
  ApiResponse<OriginsResponseType>
> {
  const response = yield call(regionsMiddleware.getOrigins, payload);
  if (response.status !== 200) {
    yield put(actions.getOriginsError());
  } else {
    const { result } = response.data;
    yield put(actions.getOriginsSuccess(result));
  }
}

function* getWorkzones({
  payload,
}: PayloadAction<string>): Generator<
  | PutEffect<{ type: string }>
  | CallEffect<AxiosResponse<ApiResponse<WorkzonesResponseType>>>,
  void,
  ApiResponse<WorkzonesResponseType>
> {
  const response = yield call(regionsMiddleware.getWorkzonesByRegion, payload);
  if (response.status !== 200) {
    yield put(actions.getWorkzonesError());
  } else {
    const { result } = response.data;
    yield put(actions.getWorkzonesSuccess(result.items));
  }
}

function* getRegionDetail({
  payload,
}: PayloadAction<string>): Generator<
  | PutEffect<{ type: string }>
  | CallEffect<AxiosResponse<ApiResponse<RegionDetailResponseType>>>,
  void,
  ApiResponse<RegionDetailResponseType>
> {
  const response = yield call(regionsMiddleware.getRegionDetail, payload);
  if (response.status !== 200) {
    yield put(actions.getRegionDetailError());
    yield put(notificationActions.showNotification(serverErrorAlert()));
  } else {
    const { result } = response.data;

    yield put(actions.getRegionDetailSuccess(result));
  }
}

function* updatePricingDetail({
  payload,
}: PayloadAction<PricingMiddlewareType>): Generator<
  | CallEffect<AxiosResponse<ApiResponse<UpdatePricingResponseType>>>
  | PutEffect<{ type: string; content: NotificationStateType }>
  | PutEffect<{ type: string }>,
  void,
  ApiResponse<PricingResponseType>
> {
  const procesData: PricingMiddlewareType = {
    ...payload,
    body: {
      ...payload.body,
      tiers: {
        day:
          payload.body.tiers?.day?.slice(1).length === 0
            ? null
            : payload.body.tiers?.day?.slice(1)!,
        week:
          payload.body.tiers?.week?.slice(1).length === 0
            ? null
            : payload.body.tiers?.week?.slice(1)!,
      },
    },
  };

  const response = yield call(
    regionsMiddleware.updatePricingDetail,
    procesData
  );
  if (response.status !== 200) {
    yield put(notificationActions.showNotification(connectionErrorAlert()));
    yield put(actions.updatePricingDetailError());
  } else {
    yield put(
      notificationActions.showNotification({
        id: "notification-changes-saved",
        level: "success",
        title: i18next.t("alerts:changesSavedTitle"),
        body: i18next.t("alerts:changesSavedMessage"),
      })
    );
    yield put(actions.getPricingDetailRequest(payload.id!));
    yield put(actions.updatePricingDetailSuccess());
    yield put(sessionActions.setDirty(false));
  }
}

function* updateRegionDetail({
  payload,
}: PayloadAction<PolygonGeneralForm>): Generator<
  | CallEffect<AxiosResponse<ApiResponse<UpdateRegionDetailResponseType>>>
  | PutEffect<{ type: string; content: NotificationStateType }>
  | PutEffect<{ type: string }>,
  void,
  ApiResponse<UpdateRegionDetailResponseType>
> {
  const response = yield call(regionsMiddleware.updateRegionDetail, payload);
  if (response.status !== 200) {
    yield put(notificationActions.showNotification(connectionErrorAlert()));
    yield put(actions.updateRegionDetailError());
  } else {
    yield put(
      notificationActions.showNotification({
        id: "notification-changes-saved",
        level: "success",
        title: i18next.t("alerts:changesSavedTitle"),
        body: i18next.t("alerts:changesSavedMessage"),
      })
    );
    yield put(actions.getRegionDetailRequest(payload.id!));
    yield put(actions.updateRegionDetailSuccess());
    yield put(sessionActions.setDirty(false));
  }
}

function* updateWorkzoneCapacity({
  payload,
}: PayloadAction<UpdateWorkzoneCapacityPayloadType>): Generator<
  | CallEffect<AxiosResponse<ApiResponse<UpdateRegionDetailResponseType>>>
  | PutEffect<{ type: string }>,
  void,
  ApiResponse<UpdateRegionDetailResponseType>
> {
  const response = yield call(
    regionsMiddleware.updateWorkzoneCapacity,
    payload
  );
  if (response.status !== 200) {
    yield put(actions.updateWorkzoneCapacityError());
  } else {
    yield put(actions.getRegionDetailRequest(payload.id));
    yield put(actions.updateWorkzoneCapacitySuccess());
  }
}

function* getPricingDetail({
  payload,
}: PayloadAction<string>): Generator<
  | PutEffect<{ type: string }>
  | CallEffect<AxiosResponse<ApiResponse<WorkzonesResponseType>>>,
  void,
  ApiResponse<PricingResponseType>
> {
  const response = yield call(regionsMiddleware.getPricingDetail, payload);
  if (response.status !== 200) {
    yield put(actions.getPricingDetailError());
    yield put(notificationActions.showNotification(serverErrorAlert()));
  } else {
    const { result } = response.data;
    const addElement: TierType[] = [{ base: 0, price: 0 }];
    const procesData: PricingType = {
      ...result,
      tiers: {
        day: result.tiers?.day
          ? addElement.concat(result.tiers?.day)
          : addElement,
        week: result.tiers?.week
          ? addElement.concat(result.tiers?.week)
          : addElement,
      },
    };
    yield put(actions.getPricingDetailSuccess(procesData));
  }
}

function* getNextRain({
  payload,
}: PayloadAction<string>): Generator<
  CallEffect<AxiosResponse<RainsResponseType>> | PutEffect<{ type: string }>,
  void,
  ApiResponse<RainsResponseType>
> {
  const response = yield call(
    regionsMiddleware.getRains,
    { type: "NEXT", limit: 1 },
    payload
  );
  if (response.status !== 200) {
    yield put(actions.getNextRainError());
  } else {
    const { result } = response.data;
    yield put(actions.getNextRainSuccess(result.items[0]));
  }
}
